// IdentityGate.jsx
// Shared across all Narbodica apps.
// If user is not identity-verified, shows a full-screen gate
// directing them to complete verification via Maia.
// Verification is on the shared User model — verify once, access everywhere.
//
// IMPORTANT: Handles 401 responses (stale tokens, password changes) by
// clearing the token and forcing re-login instead of showing verification gate.

import { useState, useEffect } from 'react';
import { API_BASE as API_BASE } from '../apiConfig';


export default function IdentityGate({ token, tokenKey = 'accessToken', children, accent = '#3b82f6' }) {
  const [status, setStatus] = useState('loading'); // loading | verified | unverified | relogin | error

  useEffect(() => {
    const t = token || localStorage.getItem(tokenKey);
    if (!t) { setStatus('verified'); return; } // Not logged in — auth screen handles this

    fetch(`${API_BASE}/api/identity/status`, {
      headers: { Authorization: `Bearer ${t}` },
    })
      .then(r => {
        // Handle auth failures — stale token, password changed, expired JWT
        if (r.status === 401 || r.status === 403) {
          return r.json().then(data => {
            // Password was changed or token is invalid — clear and force re-login
            if (data?.passwordChanged || r.status === 401) {
              localStorage.removeItem(tokenKey);
              // Also try common token keys in case app uses multiple
              ['accessToken', 'connect_token', 'gr_token', 'mp_token', 'signal_token'].forEach(k => {
                if (k !== tokenKey) return; // only clear the one we know about
              });
              setStatus('relogin');
              return null;
            }
            // 403 for other reasons (suspended, banned) — show error
            setStatus('error');
            return null;
          });
        }
        return r.json();
      })
      .then(data => {
        if (!data) return; // Already handled above
        const verified = data?.data?.verified || data?.verified || false;
        setStatus(verified ? 'verified' : 'unverified');
      })
      .catch(() => setStatus('error'));
  }, [token, tokenKey]);

  if (status === 'loading') {
    return (
      <div style={S.wrap}>
        <div style={S.spinner(accent)} />
        <p style={S.text}>Checking verification...</p>
      </div>
    );
  }

  // Stale token — force re-login
  if (status === 'relogin') {
    return (
      <div style={S.wrap}>
        <div style={S.icon}>🔒</div>
        <h2 style={S.heading}>Session expired</h2>
        <p style={S.text}>
          Your session has expired or your password was recently changed. 
          Please sign in again to continue.
        </p>
        <button onClick={() => window.location.reload()} style={S.btn(accent)}>
          Sign in
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={S.wrap}>
        <p style={{ ...S.text, color: '#fca5a5' }}>Could not check verification status.</p>
        <button onClick={() => window.location.reload()} style={S.btn(accent)}>Try again</button>
      </div>
    );
  }

  if (status === 'unverified') {
    return (
      <div style={S.wrap}>
        <div style={S.icon}>🪪</div>
        <h2 style={S.heading}>Identity verification required</h2>
        <p style={S.text}>
          Narbodica requires all users to verify their identity before accessing our apps. 
          This keeps our community safe and accountable.
        </p>
        <p style={S.text}>
          Verification takes about a minute and only needs to be done once — 
          it works across all Narbodica apps.
        </p>
        <a href="https://maia.narbodica.com.au" style={S.btn(accent)}>
          Verify with Maia
        </a>
        <p style={S.subtext}>
          Your safety is our #1 concern. We verify every user to protect 
          our community from illegal and harmful activity.
        </p>
        <p style={S.subtext}>
          Having trouble? <a href="mailto:support@narbodica.com.au" style={{ color: accent }}>support@narbodica.com.au</a>
        </p>
      </div>
    );
  }

  // Verified — render the app
  return children;
}

const S = {
  wrap: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: '#09090b',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '32px', gap: '16px', fontFamily: "'Inter', -apple-system, sans-serif",
  },
  icon: { fontSize: '48px', marginBottom: '8px' },
  heading: { fontSize: '20px', fontWeight: 600, color: '#f4f4f5', margin: 0, textAlign: 'center' },
  text: { fontSize: '14px', color: '#a1a1aa', textAlign: 'center', maxWidth: '320px', lineHeight: 1.6, margin: 0 },
  subtext: { fontSize: '11px', color: '#52525b', textAlign: 'center', maxWidth: '280px', lineHeight: 1.5, margin: 0 },
  btn: (accent) => ({
    display: 'inline-block', padding: '14px 32px',
    background: accent, borderRadius: '12px',
    color: '#fff', fontWeight: 600, fontSize: '14px',
    textDecoration: 'none', border: 'none', cursor: 'pointer',
    marginTop: '8px',
  }),
  spinner: (accent) => ({
    width: '32px', height: '32px',
    border: `2px solid ${accent}30`, borderTopColor: accent,
    borderRadius: '50%',
    animation: 'idgate-spin 0.8s linear infinite',
  }),
};

// Inject keyframes
if (typeof document !== 'undefined' && !document.getElementById('idgate-style')) {
  const style = document.createElement('style');
  style.id = 'idgate-style';
  style.textContent = '@keyframes idgate-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}
